/**
 * App.js — Root component
 *
 * Architecture at a glance
 * ───────────────────────────────────────────────────────────────────────────
 *  1.  CONSTANTS / CONFIG  – pure data, no logic
 *  2.  MODULE HELPERS      – pure functions, no `this`
 *  3.  App class
 *       constructor         – wires up state, refs, WebSocket
 *       lifecycle           – mount / unmount
 *       resize()            – single unified scale function
 *       WebSocket handlers  – onReceivingData → _routeEvent
 *       Game engines        – ALL driven by ENGINE_TABLE (one table, one method)
 *       UI helpers          – send, auth, loader, etc.
 *       render()            – thin JSX; per-game routes use ROUTE_TABLE
 */

// ─── React ────────────────────────────────────────────────────────────────────
import React from "react";
import "./code/css/app.css";

// ─── Assets ───────────────────────────────────────────────────────────────────
import BallaGamesLogo from "./assets/Preloadimges/BallaGame.webp";
import ClansagaLoader from "./assets/Preloadimges/ClansagaLoader.png";
import loginBg from "./assets/login/loginbg.jpg";
import errorAlert from "./assets/errorAlert.png";
import mobile_tilt from "./assets/mobile_tilt.svg";
import exitSoundSrc from "./assets/sounds/exit.mp3";
import betSoundSrc from "./assets/sounds/bet.mp3";
import sparkleAnimation from "./code/js/games/sparkanim";

// ─── Timer game components ────────────────────────────────────────────────────
import FunTargetTimer from "./code/js/games/timer/funTarget_timer/funTargetTimer";
import FunSoratTimer from "./code/js/games/timer/funSorat_timer/funSoratTimer";
import FunMatkaTimer from "./code/js/games/timer/funMatka_timer/funMatkaTimer";
import AmericanRouletteTimer from "./code/js/games/timer/americanRoulette_timer/americanRouletteTimer";
import EuropeanRouletteTimer from "./code/js/games/timer/europeanRoulette_timer/europeanRouletteTimer";
import DragonTigerTimer from "./code/js/games/timer/dragon_tiger_timer/dragon_tiger_Timer";
import MoneyWheelTimer from "./code/js/games/timer/money_wheel_timer/moneyWheel_timer";
import AndarBaharTimer from "./code/js/games/timer/andarBahar_timer/andarBaharTimer";
import AndarBaharClassic from "./code/js/games/timer/AB_Classic/andarBaharClassicTimer";

// ─── Non-timer game components ────────────────────────────────────────────────
import FunTarget from "./code/js/games/non_timer/funTarget/funTarget";
import FunCard from "./code/js/games/non_timer/funCard/funCard";
import FunMatka from "./code/js/games/non_timer/funMatka/funMatka";
import AmericanRoulette from "./code/js/games/non_timer/americanRoulette/americanRoulette";
import EuropeanRoulette from "./code/js/games/non_timer/europeanRoulette/europeanRoulette";
import KenoGame from "./code/js/games/non_timer/keno/keno";
import RussianPoker from "./code/js/games/non_timer/RussianPoker/RussianPoker";
import TropicalPoker from "./code/js/games/non_timer/TropicalPoker/TropicalPoker";
import OasisPoker from "./code/js/games/non_timer/oasisPoker/OasisGoldenPoker";
import CarebbeanPoker from "./code/js/games/non_timer/CaribbeanPoker/Carebianpoker";
import Baccarat from "./code/js/games/non_timer/Baccarat/Baccart";
import Bingo from "./code/js/games/non_timer/BINGO/bingo";
import JacksorBetter from "./code/js/games/non_timer/jacksorbetter/jacksorbetter";
import BlackJack from "./code/js/games/non_timer/blackjack/blackjack";
import HoldemGame from "./code/js/games/non_timer/Casinoholdem/CasinoHoldem";
import TripleChance from "./code/js/games/non_timer/tripleChance/tripleChance";
import PaiGowGame from "./code/js/games/non_timer/PaiGow/PaiGow";
import KenoMain from "./code/js/games/non_timer/KenoStake/KenoMain";
import AmarAkbarAntony from "./code/js/games/non_timer/AmarAkbarAntony/AmarAkbarAntony";
import AnimalSorat from "./code/js/games/non_timer/AnimalSorat/AnimalSorat";
import AndarBaharClassicNonTimer from "./code/js/games/non_timer/Andar_Bahar_Classic/AndarBaharClassic";
import Truco from "./code/js/games/non_timer/Truco/Truco";

// ─── Slot game components ─────────────────────────────────────────────────────
import Retrotech from "./code/js/games/slots/Retrotech/Retro";
import FruityFiesta from "./code/js/games/slots/FruityFiesta/Fruity";
import CorsairsTreasure from "./code/js/games/slots/CorsairsTreasure/CorsairsTreasure";

// ─── Shared UI ────────────────────────────────────────────────────────────────
import PayOutInfo from "./code/js/games/payoutInfo";
import HomePage from "./code/js/home/home";

// ─── Infrastructure ───────────────────────────────────────────────────────────
import { Switch, Route, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import servicConnect from "../src/code/js/websocket/service";
import { Emitter } from "../src/code/js/websocket/eventEmiter";
import ForceLogin from "../src/code/js/login/forceLogin";
import ContextAPI from "./context_api";
import {
  fetchEnglish, fetchTelugu, fetchBengali, fetchMarati,
  fetchGujarati, fetchKannada, fetchTamil, fetchMalayalam, fetchPortuguese,
} from "./code/redux/actions";
import WelcomeTrucoDialog from "./code/js/games/non_timer/Truco/WelcomeTrucoDialog";


// ═════════════════════════════════════════════════════════════════════════════
// 1. CONSTANTS / CONFIG
// ═════════════════════════════════════════════════════════════════════════════

/** All game IDs that use the card-game layout (no mobile rotate trick). */
const CARD_GAMES = new Set([
  1, 2, 3, 4, 5, 6, 7, 8, 12, 13, 14, 19, 20, 23, 24, 25, 26,
  28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 45, 46, 49, 50,
]);

/** Game IDs that receive a server-side countdown timer. */
const TIMER_GAMES = new Set([2, 3, 7, 12, 19, 20, 23, 24]);

/** Site-specific loader images. Falls back to the default logo. */
const SITE_LOADERS = { clansaga: ClansagaLoader, luckhunter: BallaGamesLogo };

/** Maps a language code (lower-case) to the matching Redux action name. */
const LANG_ACTION = {
  en: "fetchEnglish", te: "fetchTelugu", bn: "fetchBengali",
  mr: "fetchMarati", gu: "fetchGujarati", kn: "fetchKannada",
  ta: "fetchTamil", ml: "fetchMalayalam", pt: "fetchPortuguese",
};

/**
 * ENGINE_TABLE — The heart of all game routing.
 *
 * Each entry describes ONE game:
 *   resultCmd  – the WebSocket commandName that carries the game result
 *   resultFn   – the ref method to call on the game component
 *   actions    – optional map of event.action → ref method (for poker etc.)
 *   timerFn    – ref method to call when a "game.timer" ACK arrives
 *   extraState – extra keys merged into setState on result (e.g. infoText)
 *
 * The generic _handleGameEvent() function reads this table and does all the
 * work — no individual xxxGameEngine method needed.
 */
const ENGINE_TABLE = {
  // ── Fun Target ──────────────────────────────────────────────────────────
  1: { resultCmd: "FUN_CARD_RESULT", resultFn: "funCardGetResp" },
  2: { resultCmd: "FUN_TARGET_RESULT", resultFn: "funTargetTimerGetwheelresp", timerFn: "funTargetTimerGetTime" },
  5: { resultCmd: "TARGET_RESULT", resultFn: "funTargetGetwheelresp" },

  // ── Roulette ────────────────────────────────────────────────────────────
  3: { resultCmd: "FUN_ROULETTE_RESULT", resultFn: "americanRoulleteTimerGetwheelresp", timerFn: "AmericanRouletteTimerGetTime" },
  6: { resultCmd: "ROULETTE_RESULT", resultFn: "americanRoulleteGetwheelresp" },
  12: { resultCmd: "FUN_ROULETTE_RESULT", resultFn: "europeanRoulleteTimerGetwheelresp", timerFn: "EuropeanRouletteTimerGetTime" },
  13: { resultCmd: "ROULETTE_RESULT", resultFn: "europeanRoulleteGetwheelresp" },

  // ── Matka / Sorat ────────────────────────────────────────────────────────
  7: { resultCmd: "FUN_MATKA_RESULT", resultFn: "funMatkaTimerGetwheelresp", timerFn: "funMatkaTimerGetTime" },
  14: { resultCmd: "FUN_MATKA_RESULT", resultFn: "funMatkaGetwheelresp" },
  19: { resultCmd: "SORAT_RESULT", resultFn: "funSoratTimerGetwheelresp", timerFn: "funSoratTimerGetTime" },

  // ── Dragon Tiger / Money Wheel ────────────────────────────────────────────
  20: { resultCmd: "DRAGON_TIGER_RESULT", resultFn: "dragonTigerResults", timerFn: "dragonTigerTimerGetTime", passEvent: true },
  23: { resultCmd: "MONEY_WHEEL_RESULT", resultFn: "moneyWheelGetwheelresp", timerFn: "moneyWheelTimerGetTime" },

  // ── Andar Bahar ───────────────────────────────────────────────────────────
  24: {
    resultCmd: "ANDAR_BAHAR_RESULT",
    timerFn: "andarBaharTimer_GetTime",
    actions: {
      default: "andarBaharTimerGetResp",
    },
    passEvent: true,
  },
  41: {
    resultCmd: "ANDAR_BAHAR_RESULT",
    timerFn: "andarBaharTimer_GetTime",
    actions: { default: "andarBaharTimerGetResp" },
    passEvent: true,
  },
  42: {
    resultCmd: "ANDAR_BAHAR_RESULT",
    actions: {
      PARAM_BET: "checkAndarBaharClassicResult",
      ANDAR_BAHAR_BET: "dealAndCheckAndarBaharResult",
    },
    passEvent: true,
  },

  // ── Triple Chance / Bingo / Keno ──────────────────────────────────────────
  22: { resultCmd: "TRIPLE_CHANCE_RESULT", resultFn: "tripleChanceResult" },
  25: { resultCmd: "BINGO_RESULT", resultFn: "funBingoResultRes", passEvent: true },
  26: { resultCmd: "KENO_RESULT", resultFn: "kenoResults", passEvent: true, dedupeKeno: true },
  49: { resultCmd: "KENO_STAKE_RESULT", resultFn: "DrawResult", passEvent: true },

  // ── Baccarat / Truco ──────────────────────────────────────────────────────
  29: { resultCmd: "BACCARAT_RESULT", resultFn: "BetResponcebaccart", passEvent: true },
  4: { resultCmd: null /* handled inline by _routeEvent */ },

  // ── Poker variants ────────────────────────────────────────────────────────
  28: {
    resultCmd: "RUSSIAN_POKER_RESULT",
    extraState: (e) => ({ infoText: e.message }),
    actions: {
      PARAM_BET: "rpAnteResultRes",
      BET: "russianPokerresp",
      FOLD: "FoldHandresp",
      CONFIRM_FOLD: "FoldHandresp",
      REPLACE_CARDS: "rpReplaceCards",
      BUY_PLAYER_CARD: "buyplayercardresp",
      BUY_DEALER_CARD: "buydealercardResponse",
      CONFIRM_DEALER_CARD: "buyconfirmdealercardresp",
      INSURANCE: "claiminsurenceresp",
    },
    passEvent: true,
  },
  33: {
    resultCmd: "CARIBBEAN_POKER_RESULT",
    extraState: (e) => ({ infoText: e.message }),
    actions: {
      PARAM_BET: "Carebbeananterespnce",
      BET: "CarebbeanBetreapnce",
      FOLD: "FoldHandrespCaribbean",
      CONFIRM_FOLD: "FoldHandrespCaribbean",
    },
    passEvent: true,
  },
  37: {
    resultCmd: "POKER_GOLDEN_OASIS_RESULT",
    extraState: (e) => ({ infoText: e.message }),
    actions: {
      PARAM_BET: "OSAnteResultRes",
      BET: "OasisPokerBetRes",
      FOLD: "FoldHandresp",
      CONFIRM_FOLD: "FoldHandresp",
      REPLACE_CARDS: "OSReplaceCards",
    },
    passEvent: true,
    passAction: true,
  },
  39: {
    resultCmd: "TROPICAL_STUD_POKER_RESULT",
    extraState: (e) => ({ infoText: e.message }),
    actions: {
      PARAM_BET: "TPAnteResultRes",
      BET: "russianPokerrespTP",
      FOLD: "FoldHandresp",
      CONFIRM_FOLD: "FoldHandresp",
      REPLACE_CARDS: "TPReplaceCards",
      REDRAW_CARDS: "TPReplaceCards",
    },
    passEvent: true,
    passAction: true,
  },
  38: {
    resultCmd: "CASSINO_HOLDEM_RESULT",
    extraState: (e) => ({ infoText: e.message }),
    actions: { BET: "HoldembetResponse", CALL: "HoldemCallreponce", FOLD: "HoldemFoldresponce" },
    passEvent: true,
  },

  // ── Video poker (identical 3-action pattern) ──────────────────────────────
  30: { resultCmd: "JACKS_OR_BETTER_RESULT", extraState: (e) => ({ infoText: e.message }), actions: { PARAM_BET: "dealBetResultRes", KEEP_CARDS: "drawBetResult", STOP_AUTO_PLAY: "stopAutoPlayResult" }, passEvent: true },
  35: { resultCmd: "DEUCES_WILD_RESULT", extraState: (e) => ({ infoText: e.message }), actions: { PARAM_BET: "dealBetResultRes", KEEP_CARDS: "drawBetResult", STOP_AUTO_PLAY: "stopAutoPlayResult" }, passEvent: true },
  36: { resultCmd: "JOKER_POKER_RESULT", extraState: (e) => ({ infoText: e.message }), actions: { PARAM_BET: "dealBetResultRes", KEEP_CARDS: "drawBetResult", STOP_AUTO_PLAY: "stopAutoPlayResult" }, passEvent: true },
  40: { resultCmd: "TENS_OR_BETTER_RESULT", extraState: (e) => ({ infoText: e.message }), actions: { PARAM_BET: "dealBetResultRes", KEEP_CARDS: "drawBetResult", STOP_AUTO_PLAY: "stopAutoPlayResult" }, passEvent: true },
  32: { resultCmd: "BLACK_JACK_RESULT", extraState: (e) => ({ infoText: e.message }), actions: { PARAM_BET: "dealBetResultRes", MOVE_BET: "dealBetResultRes" }, passEvent: true },

  // ── Pai Gow ───────────────────────────────────────────────────────────────
  31: {
    resultCmd: "PAIGOW_RESULT",
    extraState: (e) => ({ infoText: e.message }),
    actions: {
      PARAM_SPLIT: "paiGowAnteSplitResult",
      default: "paiGowAnteResponse",
    },
    passEvent: true,
  },

  // ── Animal Sorat / Amar Akbar Anthony ────────────────────────────────────
  34: { resultCmd: "ANIMAL_SORAT_RESULT", resultFn: "checkAnimalSoratResult", passEvent: true },
  43: { resultCmd: "AAA_RESULT", resultFn: "checkAmarAkbarAntonyResult", passEvent: true },

  // ── Slots ────────────────────────────────────────────────────────────────
  45: { resultCmd: "RETRO_TECH_RESULT", resultFn: "RetroBetRes", extraState: (e) => ({ infoText: e.message }), passEvent: true },
  46: { resultCmd: "FRUITY_FIESTA_RESULT", resultFn: "RetroBetRes", extraState: (e) => ({ infoText: e.message }), passEvent: true },
  50: {
    resultCmd: "CorsairsTreasureResult",
    extraState: (e) => ({ infoText: e.message }),
    actions: { PARAM_BET: "CorsairsTreasureBetRes", MOVE_BET: "CorsairsTreasureBonusRes" },
    passEvent: true,
  },
};

/**
 * ROUTE_TABLE — Maps each game ID to its route path and component.
 * Extra props beyond the standard shared set are declared here.
 * This drives the render() method — no per-route JSX boilerplate needed.
 */
const ROUTE_TABLE = [
  { path: "/", Component: null, gameId: null }, // handled separately (HomePage)
  { path: "/home", Component: null, gameId: null }, // handled separately (HomePage)

  { path: "/targetNonTimer", Component: FunTarget, gameId: 5, extra: (p) => ({ ft_innerLimits: p.innerLimits, ft_chipValues: p.chipValues }) },
  { path: "/miniAndarBahar", Component: FunCard, gameId: 1, extra: (p) => ({ funCardGameState: p.gameState, fc_innerLimits: p.innerLimits, fc_chipValues: p.chipValues }) },
  { path: "/targetTimer", Component: FunTargetTimer, gameId: 2, extra: (p) => ({ ftt_innerLimits: p.innerLimits, ftt_chipValues: p.chipValues }) },
  { path: "/soratTimer", Component: FunSoratTimer, gameId: 19, extra: (p) => ({ fst_innerLimits: p.innerLimits, fst_chipValues: p.chipValues }) },
  { path: "/matkatimer", Component: FunMatkaTimer, gameId: 7, extra: (p) => ({ fmt_innerLimits: p.innerLimits, fmt_chipValues: p.chipValues }) },
  { path: "/matkaNonTimer", Component: FunMatka, gameId: 14, extra: (p) => ({ fm_innerLimits: p.innerLimits, fm_chipValues: p.chipValues }) },
  { path: "/americanRouletteTimer", Component: AmericanRouletteTimer, gameId: 3, extra: (p) => ({ art_innerLimits: p.innerLimits, art_chipValues: p.chipValues }) },
  { path: "/americanRoulettenon", Component: AmericanRoulette, gameId: 6, extra: (p) => ({ ar_innerLimits: p.innerLimits, ar_chipValues: p.chipValues }) },
  { path: "/europeanRouletteTimer", Component: EuropeanRouletteTimer, gameId: 12, extra: (p) => ({ ert_innerLimits: p.innerLimits, ert_chipValues: p.chipValues }) },
  { path: "/europeanRoulette", Component: EuropeanRoulette, gameId: 13, extra: (p) => ({ er_innerLimits: p.innerLimits, er_chipValues: p.chipValues }) },
  { path: "/andarBaharTimer", Component: AndarBaharTimer, gameId: 24, extra: (p) => ({ childRefAB: p.childRef, ab_timeSend: p.timesend, andarBaharTimerResult: p.targetResult, abt_innerLimits: p.innerLimits, abt_chipValues: p.chipValues }) },
  { path: "/andarBaharClassicTimer", Component: AndarBaharClassic, gameId: 41, extra: (p) => ({ childRefAB: p.childRef, ab_timeSend: p.timesend, andarBaharTimerResult: p.targetResult, abt_innerLimits: p.innerLimits, abt_chipValues: p.chipValues }) },
  { path: "/andarBaharNonTimer", Component: AndarBaharClassicNonTimer, gameId: 42, extra: (p) => ({ childRefAB: p.childRef, andarBaharTimerResult: p.targetResult, playerBalanceHandle: p.updatePlayerBalance, abt_innerLimits: p.innerLimits, abt_chipValues: p.chipValues }) },
  { path: "/dragonTiger", Component: DragonTigerTimer, gameId: 20, extra: (p) => ({ dt_timeSend: p.timesend, bet_request: p.sendBet, dt_innerLimits: p.innerLimits, dtt_chipValues: p.chipValues }) },
  { path: "/moneyWheel", Component: MoneyWheelTimer, gameId: 23, extra: (p) => ({ moneywheelResult: p.targetResult, mw_timesend: p.timesend, mwt_innerLimits: p.innerLimits, mwt_chipValues: p.chipValues }) },
  { path: "/tripleChance", Component: TripleChance, gameId: 22, extra: (p) => ({ history: p.gameState?.history, tripleChanceGameState: p.gameState, targetResult: p.targetResult }) },
  { path: "/Keno", Component: KenoGame, gameId: 26, extra: (p) => ({ kn_innerLimits: p.innerLimits, kn_chipValues: p.chipValues, kn_payouts: p.game_PayoutInfo }) },
  { path: "/bingo", Component: Bingo, gameId: 25, extra: (p) => ({ BingolResult: p.targetResult, bingo_innerLimits: p.innerLimits, bng_chipValues: p.chipValues }) },
  { path: "/russianPoker", Component: RussianPoker, gameId: 28, extra: (p) => ({ russianPokerResult: p.targetResult, rp_minbonusbet: p.minBonusBet, rp_chipValues: p.chipValues, previousGamestateCardLength6: p.gameState }) },
  { path: "/CarebbeanPoker", Component: CarebbeanPoker, gameId: 33, extra: (p) => ({ CarebbeanPokerResult: p.targetResult, cb_chipValues: p.chipValues, payoutInfoscrbn: p.game_PayoutInfo }) },
  { path: "/goldenoasispoker", Component: OasisPoker, gameId: 37, extra: (p) => ({ tropicalPokerResult: p.targetResult, os_chipValues: p.chipValues, OS_payouts: p.game_PayoutInfo, previousGamestateCardLength6: p.gameState }) },
  { path: "/tropicalPoker", Component: TropicalPoker, gameId: 39, extra: (p) => ({ tropicalPokerResult: p.targetResult, tp_chipValues: p.chipValues, TP_payouts: p.game_PayoutInfo, previousGamestateCardLength6: p.gameState }) },
  { path: "/Baccarat", Component: Baccarat, gameId: 29, extra: (p) => ({ BaccaratResult: p.targetResult, bc_chipValues: p.chipValues, baccartpayoutin: p.game_PayoutInfo, baccarat_innerLimits: p.innerLimits }) },
  { path: "/jacksrbetter", Component: JacksorBetter, gameId: 30, extra: (p) => ({ jrb_chipValues: p.chipValues }) },
  { path: "/deuceswild", Component: JacksorBetter, gameId: 35, extra: (p) => ({ jrb_chipValues: p.chipValues }) },
  { path: "/jokerpoker", Component: JacksorBetter, gameId: 36, extra: (p) => ({ jrb_chipValues: p.chipValues }) },
  { path: "/tensrbetter", Component: JacksorBetter, gameId: 40, extra: (p) => ({ jrb_chipValues: p.chipValues }) },
  { path: "/blackjack", Component: BlackJack, gameId: 32, extra: (p) => ({ jrb_chipValues: p.chipValues }) },
  { path: "/amarAkbarAntony", Component: AmarAkbarAntony, gameId: 43, extra: (p) => ({ aaa_timeSend: p.timesend, AmarAkbarAntonyResult: p.targetResult, callTakeAmount: p.takeAmountResult, playerBalanceHandle: p.updatePlayerBalance, aaa_innerLimits: p.innerLimits, aaa_chipValues: p.chipValues }) },
  { path: "/animalSorat", Component: AnimalSorat, gameId: 34, extra: (p) => ({ animalSoratResult: p.targetResult, playerBalanceHandle: p.updatePlayerBalance, callTakeAmount: p.takeAmountResult, as_innerLimits: p.innerLimits, as_chipValues: p.chipValues }) },
  { path: "/retrotech", Component: Retrotech, gameId: 45, extra: (p) => ({ os_chipValues: p.chipValues, TP_payouts: p.game_PayoutInfo, previousGamestateCardLength6: p.gameState }) },
  { path: "/fruityfiesta", Component: FruityFiesta, gameId: 46, extra: (p) => ({ os_chipValues: p.chipValues, TP_payouts: p.game_PayoutInfo, previousGamestateCardLength6: p.gameState }) },
  { path: "/corsairstreasure", Component: CorsairsTreasure, gameId: 50, extra: (p) => ({ os_chipValues: p.chipValues, TP_payouts: p.game_PayoutInfo, previousGamestateCardLength6: p.gameState }) },
  { path: "/paigow", Component: PaiGowGame, gameId: 31, extra: (p) => ({ gamstateHandle: p.gamstateHandle, gameState: p.gameState, isMobile: p.isMobile || p.isIphone, pg_chipValues: p.chipValues, pg_minBet: p.minBetAmt, pg_maxBet: p.maxBetAmt, pg_minBetLbl: p.minBetLbl, pg_maxBetLbl: p.maxBetLbl, pg_innerLimits: p.innerLimits, language: p.language, infoText: p.infoText }) },
  // KenoStake, Holdem, Truco rendered separately (non-standard prop shapes)
];

/** Default state — defined outside the class so the constructor is lean. */
const INITIAL_STATE = {
  progressValue: 0,
  MainLoaderImg: false,
  showTrucoGame: false,
  websocketLoader: false,
  enterGameLoader: false,
  logInBgLoader: true,
  heightFix: true,
  PortraiteViewChange: false,
  showLoading: true,
  showLogin: false,
  logBtnLoad: false,
  forceLog: false,
  isPlayerLogin: false,
  user: "",
  name: "",
  password: "",
  playerId: 0,
  alertText: "",
  opacity: 1,
  lobbyInfo: {},
  balance: 0,
  gameState: null,
  targetResult: null,
  gameEntry: "in",
  infoText: "Play and Njoy!",
  closePopup: false,
  showOkButton: false,
  popupMessage: "",
  popupBtnName: "",
  error_code: "",
  isPlayerBanned: false,
  deviceBlocked: false,
  gameUnderMaintanance: false,
  gameStateUpdate: false,
  isSocketClosed: false,
  gameData: {},
  webSiteRetryCount: 0,
  savedNickname: null,
  showTrucoWelcome: false
};


// ═════════════════════════════════════════════════════════════════════════════
// 2. MODULE-LEVEL HELPERS  (pure functions — no `this`)
// ═════════════════════════════════════════════════════════════════════════════

/** Returns { isMobile, isIphone } based on the current user-agent. */
function detectDevice() {
  const ua = navigator.userAgent;
  if (/Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return { isMobile: true, isIphone: false };
  if (/iPhone|iPad|iPod/i.test(ua)) return { isMobile: false, isIphone: true };
  return { isMobile: false, isIphone: false };
}

/** Reads all recognised URL query params and returns them as a plain object. */
function parseURLParams() {
  const p = new URLSearchParams(window.location.search);
  return {
    gameId: p.get("gameId"), playerToken: p.get("playerToken"),
    playerId: p.get("playerId"), site: p.get("site"),
    lang: p.get("lang"), balance: p.get("balance"),
  };
}

/**
 * Scales a DOM element to fill (winW × winH) while preserving aspect ratio.
 * Centres the result using margin.
 */
function scaleToFit(el, origW, origH, winW, winH) {
  const scale = Math.min(winW / origW, winH / origH);
  el.style.transformOrigin = "left top";
  el.style.transform = `scale(${scale})`;
  el.style.width = `${origW}px`;
  el.style.height = `${origH}px`;
  el.style.marginLeft = `${(winW - origW * scale) / 2}px`;
  el.style.marginTop = `${(winH - origH * scale) / 2}px`;
}

/** Converts a raw number to a K / M / B label string. */
function toLabel(n) {
  n = Number(Number(n).toFixed(2));
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return n;
}

/** Shallow shorthand for sessionStorage.getItem. */
const ss = (key) => sessionStorage.getItem(key);

/** Returns true when two arrays contain the same values in the same order. */
function arraysEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}


// ═════════════════════════════════════════════════════════════════════════════
// 3. APP COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
class App extends React.Component {

  // ─── Constructor ──────────────────────────────────────────────────────────
  constructor(props) {
    super(props);

    // Parse URL once
    const url = parseURLParams();
    if (url.gameId) { this.activeGameId = Number(url.gameId); sessionStorage.setItem("gameId", url.gameId); }
    else { this.activeGameId = Number(4); sessionStorage.setItem("gameId", 4); }
    if (url.playerToken) sessionStorage.setItem("playerToken", url.playerToken);
    if (url.playerId) sessionStorage.setItem("playerId", url.playerId);
    if (url.site) { sessionStorage.setItem("siteId", url.site); }
    if (url.lang) {
      sessionStorage.setItem("myLang", url.lang);
      const action = LANG_ACTION[url.lang.toLowerCase()];
      this.props[action ?? "fetchEnglish"]?.();
    }

    // Device detection
    const { isMobile, isIphone } = detectDevice();
    this.isMobile = isMobile;
    this.isIphone = isIphone;
    this.siteId = url.site || ss("siteId") || "";

    // Bet configuration (updated live by server ACKs)
    this.minBet = 1;
    this.maxBet = 500_000;
    this.minBetLbl = "";
    this.maxBetLbl = "";
    this.innerLimits = "1-5000|1-5000|1-5000|1-5000|1-5000|1-5000";
    this.chipValues = "1,5,10,25,50,100,500,1000,5000";
    this.minBonusBet = 0;
    this.game_PayoutInfo = [];

    // Flags
    this.gameRefs = {};        // gameId → ref
    this.timersecondsIn = 0;
    this.spectatorCount = 0;
    this.systemOS = " ";
    this.gameEntryOutSide = false;
    this.receiveDataFromWs = false;
    this.reconnectAllowed = false;
    this.reconnectAllowedTimeIntervel = null;
    this.fixHight = window.innerHeight;
    this.gameBox = null;
    this.showpopIniterror = false;
    this.saveURL = window.location.search;
    this.PortraiteView = window.innerWidth < 768 && window.innerWidth < window.innerHeight;

    // Audio
    this.exitSound = new Audio(exitSoundSrc);
    this.loginSound = new Audio(betSoundSrc);

    // Refs
    this.homeRef = React.createRef();
    this.PayOutInfo = React.createRef();

    // WebSocket service
    this.connectionTo = new servicConnect();
    let playerId = ss("playerId")
    this.state = {
      ...INITIAL_STATE,
      gameId: Number(this.activeGameId) || Number(ss("gameId")) || 4,
      savedNickname: playerId || null,
      showTrucoWelcome: !Boolean(playerId),
      MainLoaderImg: Boolean(playerId),
      showTrucoGame: Boolean(playerId)
    };
    this.activeGameId = JSON.parse(ss("gameId"));
    this.loadBg();
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────
  componentDidMount() {
    window.addEventListener("offline", this.checkNetworkStatus);
    window.addEventListener("resize", this.handleResize);
    window.addEventListener("contextmenu", (e) => e.preventDefault());
    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    this.handleResize();
    this._applyOrientationUI();
    this._startProgressBar(10, 40);   // fill to 10% during initial load

    if ([20, 23, 26].includes(this.activeGameId)) this._preloadSparkAnim();

    document.getElementById("awayPopup").style.display = "none";
    if (this.state.MainLoaderImg) {
      this._connectWebSocket();
    }

    // Intercept back-button navigations
    this.unlisten = this.props.history.listen((_, action) => {
      if (action === "POP") window.location.replace(`${window.location.origin}/home`);
    });
  }

  componentWillUnmount() {
    window.removeEventListener("offline", this.checkNetworkStatus);
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    clearTimeout(this.reconnectAllowedTimeIntervel);
    this.unlisten?.();
  }

  // ─── Small private helpers ─────────────────────────────────────────────────
  _startProgressBar(target, interval) {
    const id = setInterval(() => {
      if (this.state.progressValue >= target) { clearInterval(id); return; }
      this.setState((prev) => ({ progressValue: prev.progressValue + 1 }));
    }, interval);
  }

  _applyOrientationUI() {
    const el = document.getElementById("isMobileTrue");
    const isCardGame = CARD_GAMES.has(Number(this.state.gameId));

    const update = (angle) => {
      if (el) el.style.display = (!isCardGame && angle === 90) ? "block" : "none";
    };

    window.addEventListener("orientationchange", () => update(window.screen.orientation?.angle), false);
    if (el) el.style.display = (this.isMobile && window.screen.orientation?.angle === 90 && !isCardGame) ? "block" : "none";
  }

  _preloadSparkAnim() {
    for (let n = 1; n <= 100; n++) {
      new Image().src = sparkleAnimation[`sparkanimation${n}`];
    }
  }

  // ─── Resize / scale logic ──────────────────────────────────────────────────
  handleResize = () => {
    this.PortraiteView = window.innerWidth < window.innerHeight;
    this.setState({ PortraiteViewChange: true });
    this.resize();
  };

  resize = () => {
    const winW = window.innerWidth;
    const winH = window.innerHeight;
    this.fixHight = winH;
    this.gameBox = document.getElementById("gameBoxMain");

    // Always keep mainWindow height locked to viewport height
    const mainWin = document.getElementById("mainWindow");
    if (mainWin) mainWin.style.height = `${winH}px`;
    if (!this.gameBox) return;

    const isCardGame = CARD_GAMES.has(Number(this.state.gameId));
    const landscape = winW > winH;

    if ((this.isMobile || this.isIphone) && !isCardGame) {
      // Non-card mobile: rotate 90° so landscape content appears in portrait
      const scale = winH / 2350;
      const shifted = (winH / 2350) * 1250;
      const marginL = shifted + ((shifted - winW) / 2) * -1;
      this.gameBox.style.transformOrigin = "left top";
      this.gameBox.style.transform = `scale(${scale}) rotate(90deg)`;
      this.gameBox.style.marginLeft = `${marginL}px`;
      return;
    }

    if (!isCardGame) {
      // Desktop non-card: scale-to-fit 2350 × 1250 canvas
      scaleToFit(this.gameBox, 2350, 1250, winW, winH);
      return;
    }

    // Card game: pick dimensions by orientation, then scale-to-fit
    const isWindows = navigator.userAgentData?.platform === "Windows";

    let origW, origH;
    // if (isWindows) {
    //   // On Windows, some games support portrait flip
    //   const portraitOk = new Set([1, 2, 3, 5, 6, 7, 12, 13, 14, 19, 20, 23, 24, 25, 26, 31, 34, 38, 41, 42, 43, 49]);
    //   origW = (!landscape && portraitOk.has(Number(this.state.gameId))) ? 1250 : 2350;
    //   origH = origW === 1250 ? 2350 : 1250;
    // } else {
    origW = landscape ? 2350 : 1250;
    origH = landscape ? 1250 : 2350;
    // }

    scaleToFit(this.gameBox, origW, origH, winW, winH);
  };

  // ─── WebSocket ─────────────────────────────────────────────────────────────
  _connectWebSocket() {
    clearTimeout(this.ActiveWebsocket);
    this.connectionTo.connection();
    Emitter.on(this.connectionTo.OnReceivingData, this._onData);
    Emitter.on(this.connectionTo.ConnectionEstd, this._onConnected);
    Emitter.on(this.connectionTo.ReceiveNoConnection, this._onNoConnection);
  }

  _onConnected = () => {
    this.setState({ websocketLoader: false });
    this.resize();
  };

  _onNoConnection = () => this.setState({ websocketLoader: true });

  checkNetworkStatus = () => {
    if (navigator.onLine) return;
    this.setState((prev) => ({
      isSocketClosed: true,
      webSiteRetryCount: prev.webSiteRetryCount + 1,
      popupMessage: "Slow network — please check your connection or refresh.",
      showOkButton: true,
      popupBtnName: "Refresh",
      closePopup: true,
      websocketLoader: false,
    }));
    setTimeout(() => document.location.reload(), 5000);
  };

  handleVisibilityChange = () => {
    if (document.visibilityState !== "visible") return;
    if (TIMER_GAMES.has(Number(this.state.gameId))) this.getTimer();
    if (this.connectionTo._connection?.readyState > 1 && this.state.webSiteRetryCount < 5) {
      this.setState((prev) => ({ webSiteRetryCount: prev.webSiteRetryCount + 1 }));
    }
  };

  // ─── Incoming data router ──────────────────────────────────────────────────
  /**
   * Single entry point for all WebSocket messages.
   * Keeps concerns separated:
   *   401 → show error popup
   *   200 → successful login, enter game
   *   close → exit
   *   otherwise → route to game engine
   */
  _onData = (event) => {
    if (!event) return;

    if (event.status === 401) {
      this.setState({ popupMessage: event.msg, closePopup: true, error_code: "game.under.maintenance" });
      return;
    }

    if (event.status === 200) {
      const { sessionId, playerId } = event.data;
      sessionStorage.setItem("sessionId", sessionId);
      sessionStorage.setItem("playerId", String(playerId));
      this.gameEntryOutSide = true;
      this.setState({ playerId }, () => {
        this._send({ CN: "ENTER_GAME", sessionId, object: { playerId, gameId: Number(this.state.gameId) } });
        this._send({ CN: "GET_GAME_STATE", sessionId, object: { playerId, gameId: Number(this.state.gameId) } });
        this._send({ CN: "GET_PLAYER_INFO", sessionId, object: { playerId, gameId: Number(this.state.gameId) } });
        // const cfg = ROUTE_TABLE.find(r => r.gameId === this.state.gameId);
        // if (cfg) this.props.history.replace(cfg.path);
      });
      return;
    }

    if (event.type === "close") { this.gameCloseHandler(); return; }

    // Truco: special non-standard game handled outside ENGINE_TABLE
    if (event.type && event.payload !== null && this.state.gameId === 4) {
      this.gameEntryOutSide = true;
      this.setState({ closePopup: false, popupMessage: "", websocketLoader: false }, () => {
        this.props.history.replace("/");
        // this.props.history.replace("/truco");
        this._callRef(4, "checkGameResponse", event);
      });
      return;
    }

    // Standard game event routing
    this._routeEvent(event);

    if (!this.receiveDataFromWs) {
      this._enterGameFromURL();
      this.receiveDataFromWs = true;
    }

    this.setState({ closePopup: false, popupMessage: "", websocketLoader: false });
  };

  /**
   * Routes a decoded WebSocket event to the appropriate handler method
   * based on its commandName.
   */
  _routeEvent = (event) => {
    const gameId = Number(this.state.gameId);
    if (!CARD_GAMES.has(gameId)) return;

    switch (event.commandName) {
      case "GAME_STATE": return this._onGameState(event, gameId);
      case "ACKNOWLEDGE": return this._onAcknowledge(event, gameId);
      case "PLAYER_INFO": return this._onPlayerInfo(event, gameId);
      default: return this._handleGameEvent(event, gameId);
    }
  };

  // ─── Shared command handlers ───────────────────────────────────────────────
  _onGameState = (event, gameId) => {
    if (event.timer && TIMER_GAMES.has(gameId)) this.getTimer();
    this.setState({ gameState: event, heightFix: true }, () => {
      this._callRef(gameId, "historyUpdate");
      if (this.state.gameEntry === "out" && this.state.gameState !== null) {
        this.gameEntryOutSide = true;
      }
      this._handleGameEvent(event, gameId);
    });
  };

  _onPlayerInfo = (event, gameId) => {
    const { loginName: user, playerId, balance } = event;
    sessionStorage.setItem("playerId", String(playerId));
    sessionStorage.setItem("username", user);
    this.setState({ balance, user }, () => {
      if (this.state.activeProfile) this.componentStateHandler("loginSuccess", { name: user, playerId });
      this._callRef(gameId, "updatePlayerInfo");
    });
  };

  _onAcknowledge = (event, gameId) => {
    // Update bet config whenever server sends fresh limits
    if (event.minBet) {
      this.minBet = event.minBet;
      this.maxBet = event.maxBet;
      this.minBetLbl = toLabel(event.minBet);
      this.maxBetLbl = toLabel(event.maxBet);
      this.innerLimits = event.innerLimits;
      this.chipValues = event.chipValues;
      this.minBonusBet = event.minBonus;
      if (event.payoutInfos?.length) this.game_PayoutInfo = event.payoutInfos;
      else if (event.kenoPayouts?.length) this.game_PayoutInfo = event.kenoPayouts;
      else if (event.kenoStakePayoutsMap?.length) this.game_PayoutInfo = event.kenoStakePayoutsMap;
    }

    if (!event.ackCode) return;

    // Route specialised ack codes
    const ackHandlers = {
      "game.under.maintenance": () => this.showGameMaintanance(event.ackMessage),
      "device.blocked": () => this.showDeviceBlocked(),
      "player.session.closed": () => this.showDeviceBlocked(),
      "exception.occured": () => this.gameCloseHandler(),
      "player.is.banned": () => this.playerBanned(),
      "game.timer": () => {
        this.timersecondsIn = Math.round((Number(event.ackMessage) + 500) / 1000);
        // Forward timer event to the active game ref (timerFn from ENGINE_TABLE)
        const cfg = ENGINE_TABLE[gameId];
        if (cfg?.timerFn) this._callRef(gameId, cfg.timerFn);
      },
      "game.bet.accepted": () => this._onBetMessage(event, gameId),
      "game.bet.rejected.takeAmount": () => this._onBetMessage(event, gameId),
      "game.bet.rejected": () => this._onBetMessage(event, gameId),
      "game.bet.invalid": () => this._callRef(gameId, "updateMessage", event.ackMessage),
    };
    ackHandlers[event.ackCode]?.();
  };

  _onBetMessage = (event, gameId) => {
    const lang = this.props.language;
    let msg;
    if (event.ackCode === "game.bet.accepted") {
      const amt = event.ackMessage.split("is")[1]?.replace(",", "");
      msg = `${lang?.BetAccepted ?? "Bet Accepted"}${amt}`;
    } else {
      msg = {
        "Bet Rejected!! Time Over": lang?.BetRejTimeOver,
        "Bets Rejected !! Due to {0}": lang?.InvalidBet,
        "Bet Rejected": lang?.BetRej,
        "Bet rejected!!.You have already placed BET for this Round.": lang?.AlreadyBetPlaced,
      }[event.ackMessage] ?? event.ackMessage;
    }
    this._callRef(gameId, "updateMessage", msg);
  };

  // ─── Universal game event handler ─────────────────────────────────────────
  /**
   * Reads ENGINE_TABLE[gameId] and dispatches the event to the correct
   * ref method.  Replaces ~30 individual xxxGameEngine methods.
   *
   * Flow:
   *   1. Look up the config for this gameId
   *   2. If commandName matches resultCmd → setGameResults, then call:
   *        a. actions[event.action]  (poker / multi-action games)
   *        b. resultFn               (simple games)
   *   3. Otherwise call common GAME_STATE / PLAYER_INFO / updateMessage handlers
   */
  _handleGameEvent = (event, gameId) => {
    const cfg = ENGINE_TABLE[gameId];
    if (!cfg) return;

    if (event.commandName === cfg.resultCmd) {
      // Keno deduplication guard
      if (cfg.dedupeKeno) {
        const cached = JSON.parse(localStorage.getItem("myArray"));
        if (arraysEqual(cached, event.resultNo)) return;
        localStorage.setItem("myArray", JSON.stringify(event.resultNo));
      }

      const extra = cfg.extraState?.(event) ?? {};
      const newState = { targetResult: event, ...extra };

      this.setState(newState, () => {
        if (cfg.actions) {
          // Action-based dispatch (poker, blackjack, slots with MOVE_BET, etc.)
          const fn = cfg.actions[event.action] ?? cfg.actions.default;
          if (fn) {
            // Some engines (oasis, tropical) need the action forwarded as a second arg
            cfg.passAction
              ? this._callRef(gameId, fn, event, event.action)
              : this._callRef(gameId, fn, cfg.passEvent ? event : undefined);
          }
        } else if (cfg.resultFn) {
          this._callRef(gameId, cfg.resultFn, cfg.passEvent ? event : undefined);
        }
      });
      return;
    }

    // Fallback: common GAME_STATE / PLAYER_INFO / updateMessage
    if (event.commandName === "GAME_STATE") this._callRef(gameId, "updateGameState");
    if (event.commandName === "PLAYER_INFO") this._callRef(gameId, "updatePlayerInfo");
    if (event.commandName === "ERROR_INFO") {
      const msg = this.props.language?.[event?.errorCode] ?? event?.errorMessage;
      this.setState({ infoText: msg }, () => this._callRef(gameId, "updateMessage"));
    }
  };

  // ─── Ref helper ────────────────────────────────────────────────────────────
  /** Calls a method on the active game's ref, if it exists. */
  _callRef = (gameId, fn, ...args) => this.gameRefs[gameId]?.[fn]?.(...args);

  handleGameRef = (ref) => {
    const { gameId } = this.state;
    if (ref) this.gameRefs[gameId] = ref;
    else delete this.gameRefs[gameId];
  };

  // ─── WebSocket send helper ─────────────────────────────────────────────────
  _send = (body) => this.connectionTo.sendof(body);

  // ─── Game lifecycle messages ───────────────────────────────────────────────
  getTimer = () => this._send({ CN: "GET_GAME_TIMER", sessionId: ss("sessionId"), object: { gameId: Number(this.state.gameId), playerId: this.state.playerId } });
  getGameState = () => this._send({ CN: "GET_GAME_STATE", sessionId: ss("sessionId"), object: { gameId: this.state.gameId, playerId: Number(this.state.playerId) } });
  gamstateHandleMethod = () => this.getGameState();
  updatePlayerBalance = () => this._send({ CN: "GET_PLAYER_INFO", sessionId: ss("sessionId"), object: { gameId: Number(this.state.gameId), playerId: Number(this.state.playerId) } });
  takeAmountResult = () => this._send({ CN: "TAKE_AMOUNT", sessionId: ss("sessionId"), object: { gameId: this.state.gameId, playerId: Number(this.state.playerId) } });
  sendBetRequests = (body) => this._send(body);
  getGameLobby = () => this._send({ CN: "GET_LOBBY_INFO", sessionId: ss("sessionId"), object: { playerId: Number(ss("playerId")) } });

  gameOpenHandler = (gameId) => {
    this.exitSound.play();
    this._send({ CN: "ENTER_GAME", sessionId: ss("sessionId"), object: { playerId: Number(ss("playerId")), gameId } });
  };

  gameCloseHandler = () => {
    if (this.state.gameId) {
      this._send({ CN: "EXIT_GAME", sessionId: ss("sessionId"), object: { playerId: Number(ss("playerId")), gameId: this.state.gameId } });
    }
    this.closeAllGames();
    this.setState({ gameStateUpdate: false });
    window.parent.close();
  };

  closeAllGames = () => this.setState({ gameId: null });
  closeGames = () => { clearTimeout(this.ActiveWebsocket); window.parent.close(); };
  refreshGame = () => window.open(ss("reloadUrl"), "_self");

  forcedLoginCheck = () => {
    this._send({ CN: "FORCE_LOGIN", sessionId: "", object: { system: this.systemOS, physicalAdd: "28-F1-0E-3B-7F-A0", loginName: this.state.name, password: this.state.password } });
    this.setState({ forceLog: false });
  };

  // ─── Popup / error state helpers ───────────────────────────────────────────
  showGameMaintanance = (msg) => {
    this.setState({
      popupMessage: this.props.language?.GameMainenanceMsg ?? msg,
      closePopup: true,
      error_code: "game.under.maintenance",
      popupBtnName: this.props.language?.Ok,
      gameUnderMaintanance: false,
    });
  };

  showDeviceBlocked = () => {
    this.setState({ popupMessage: this.state.gameData?.ackMessage, closePopup: true, error_code: "device.is.blocked" });
  };

  playerBanned = () => {
    this.setState({ isPlayerBanned: false, popupMessage: this.state.gameData?.ackMessage, closePopup: true, error_code: "player.is.banned" });
  };

  updateSpectatorCount_fn = (betAmount) => {
    this.spectatorCount = betAmount === 0 ? (this.spectatorCount + 1 >= 3 ? 0 : this.spectatorCount + 1) : 0;
  };

  closeWin = () => {
    window.close();
    this.setState({ closePopup: false, alertText: "" });
    const { error_code } = this.state;
    if (["player.is.banned", "device.is.blocked"].includes(error_code)) {
      this._send({ CN: "LOGOUT", sessionId: ss("sessionId"), object: { playerId: ss("playerId") } });
      this.setState({ showLogin: true, opacity: 1, logBtnLoad: false, user: "", playerId: "", alertText: "" });
    }
    if (["game.under.maintenance", "session.closed"].includes(error_code)) {
      this.gameCloseHandler();
    }
  };

  // ─── Auth state machine ────────────────────────────────────────────────────
  componentStateHandler = (action, value) => {
    const clearAuth = { opacity: 1, logBtnLoad: false, user: "", playerId: "" };

    const handlers = {
      loaded: () => this.setState({ showLoading: false, showLogin: false }),
      showLog: () => this.setState({ showLogin: true }),
      hideLog: () => this.setState({ showLogin: false }),
      loginFormSubmission: () => this.setState({ showLoading: false, showLogin: true, logBtnLoad: true, alertText: "Logging In Please Wait…" }),
      loginError: () => this.setState({ showLoading: false, showLogin: true, ...clearAuth, alertText: value?.error }),
      SessionExitst: () => this.setState({ showLoading: false, showLogin: true, ...clearAuth, alertText: value?.error }),
      loginDetails: () => this.setState({ name: value.username, password: value.password }),
      loginSuccess: () => {
        this.exitSound.play();
        this.showLobby = true;
        this.setState({ showLogin: false, ...clearAuth, alertText: "", user: value.name, playerId: value.playerId });
      },
      logOutSuccess: () => {
        this.exitSound.play();
        this._send({ CN: "LOGOUT", sessionId: ss("sessionId"), object: { playerId: ss("playerId") } });
        this.closeAllGames();
        this.showLobby = false;
        this.setState({ showLogin: true, ...clearAuth, alertText: value?.error, isPlayerLogin: false });
      },
      SessionTimeOut: () => {
        this._callRef(this.state.gameId, "gameSessionClosed");
        this.setState({ ...clearAuth, alertText: this.props.language?.Session_TimeOut });
      },
    };

    handlers[action]?.();
  };

  // ─── Loader ────────────────────────────────────────────────────────────────
  loadBg() {
    const img = new window.Image();
    img.src = loginBg;
    img.onload = () => this.setState({ logInBgLoader: false });
  }

  assestsLoaderFunction = () => {
    this.PayOutInfo?.setPayoutData?.(this.game_PayoutInfo, this.state.gameId);
    this.showpopIniterror = false;
    const INCREMENT = 100 / (1500 / 30);

    const id = setInterval(() => {
      this.setState((prev) => {
        if (prev.progressValue >= 100) {
          clearInterval(id);
          return { MainLoaderImg: false, enterGameLoader: false, progressValue: 100 };
        }
        return { progressValue: Math.min(prev.progressValue + INCREMENT, 100) };
      });
    }, 30);
  };

  // ─── Operator entry flow ───────────────────────────────────────────────────
  _enterGameFromURL = () => {
    if (this.saveURL.includes("profile")) return;
    const url = parseURLParams();
    this.setState({ enterGameLoader: true, heightFix: true, gameEntry: "out" });
    if (url.gameId) this.state.gameId = url.gameId; // intentional pre-auth set
    this.sendBody = { operatorId: "10.10.20.68", playerToken: url.playerToken, balance: url.balance, system: this.systemOS, gameId: Number(this.state.gameId), site: url.site, lang: url.lang, webSocketSessionId: ss("indieSessionId") };
  };

  // ─── Render helpers ────────────────────────────────────────────────────────
  /**
   * Returns props that every game component receives.
   * Keeping this in one place means adding / renaming a prop requires
   * a single change here.
   */
  _sharedProps() {
    const { state } = this;
    return {
      ref: this.handleGameRef,
      childRef: this.handleGameRef,
      network: this.connectionTo,
      user: state.user,
      balance: state.balance,
      playerId: state.playerId,
      gameId: state.gameId,
      gameState: state.gameState,
      targetResult: state.targetResult,
      infoText: state.infoText,
      gameEntry: state.gameEntry,
      isMobile: this.isMobile,
      isIphone: this.isIphone,
      language: this.props.language,
      minBetAmt: this.minBet,
      maxBetAmt: this.maxBet,
      minBetLbl: this.minBetLbl,
      maxBetLbl: this.maxBetLbl,
      chipValues: this.chipValues,
      innerLimits: this.innerLimits,
      game_PayoutInfo: this.game_PayoutInfo,
      assestsLoaderFunction: this.assestsLoaderFunction,
      action: this.gameCloseHandler,
      gamstateHandle: this.gamstateHandleMethod,
      game_maintanance: this.showGameMaintanance,
      player_banned: this.playerBanned,
      device_blocked: this.showDeviceBlocked,
      isPlayerBanned: state.isPlayerBanned,
      isdeviceBlocked: state.deviceBlocked,
      gameUnderMaintanance: state.gameUnderMaintanance,
      spectatorCount: this.updateSpectatorCount_fn,
      timesend: this.timersecondsIn,
      sendBet: this.sendBetRequests,
      updatePlayerBalance: this.updatePlayerBalance,
      takeAmountResult: this.takeAmountResult,
      minBonusBet: this.minBonusBet,
    };
  }

  handleNicknameSubmit = (name) => {
    sessionStorage.setItem("playerId", name)
    this.setState({
      MainLoaderImg: true,
      websocketLoader: true,
      showTrucoGame: true,
      playerId: name,
      savedNickname: name,
      showTrucoWelcome: false
    })
    this._connectWebSocket();
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  render() {
    const { state } = this;
    const sp = this._sharedProps();
    const showLoader = state.MainLoaderImg || state.websocketLoader ||
      (state.enterGameLoader && state.gameEntry === "out");

    const popupClass = [
      "popupWin",
      state.showOkButton ? "popUpRefresh" : "",
      state.closePopup ? "block" : "none",
    ].join(" ").trim();

    return (
      <React.Fragment>
        <div>

          {/* ── Main viewport window ── */}
          <div
            id="mainWindow"
            className="fgaLoginMain"
            style={{ height: state.heightFix ? `${this.fixHight}px` : "auto", overflow: state.heightFix ? "hidden" : "auto" }}
          >
            <div className="gameBoxMain" id="gameBoxMain">
              {this.gameEntryOutSide && (
                <div className="fd">

                  {/* Session-closed / network popup */}
                  <div className={popupClass}>
                    <div className="session_closed">
                      <img className="errorPopup_session_closed" src={errorAlert} alt="Error" />
                      <div className="fd session_closed_text_container">{state.popupMessage}</div>
                      {(!navigator.onLine || state.showOkButton) && (
                        <div className="fd">
                          <button
                            className="btn_2 btn_myAcountconnection m_t_15"
                            onClick={() => state.showOkButton ? document.location.reload() : this.closeGames()}
                          >
                            {state.showOkButton ? state.popupBtnName : "OK"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="appMain">
                    <ContextAPI.Provider value={state}>
                      <div className={state.forceLog ? "coverLog showCover" : "coverLog hideCover"} />

                      <Switch>

                        {/* Home (two paths, same component) */}
                        {/* {["/", "/home"].map((path) => (
                          <Route key={path} exact path={path}>
                            <HomePage
                              ref={this.homeRef}
                              websocketLoader={state.websocketLoader}
                              isPlayerLogin={state.isPlayerLogin}
                              games={state.lobbyInfo}
                              balance={state.balance}
                              network={this.connectionTo}
                              user={state.user}
                              playerId={state.playerId}
                              action={this.componentStateHandler}
                              gameOpenHandler={this.gameOpenHandler}
                            />
                          </Route>
                        ))} */}

                        {/* ── Data-driven game routes ──────────────────────── */}
                        {ROUTE_TABLE.filter(r => r.Component).map(({ path, Component, extra }) => (
                          <Route key={path} exact path={path}>
                            <Component {...sp} {...(extra ? extra(sp) : {})} />
                          </Route>
                        ))}

                        {/* ── Hand-crafted routes (non-standard prop shapes) ─ */}
                        <Route exact path="/kenoStake">
                          <KenoMain
                            ref={this.handleGameRef}
                            childRef={this.handleGameRef}
                            assestsLoaderFunction={this.assestsLoaderFunction}
                            keno_stake_payouts={this.game_PayoutInfo}
                            Keno_stake_chipValues={this.chipValues}
                            Keno_stake_minBet={this.minBet}
                            Keno_stake_maxBet={this.maxBet}
                            playerId={state.playerId}
                            balance={state.balance}
                            gameId={state.gameId}
                            network={this.connectionTo}
                            user={state.user}
                            gamestate={state.gameState}
                          />
                        </Route>

                        <Route exact path="/Holdem">
                          <HoldemGame
                            ref={this.handleGameRef}
                            network={this.connectionTo}
                            user={state.user}
                            balance={state.balance}
                            playerId={state.playerId}
                            gameId={state.gameId}
                            gamstateHandle={this.gamstateHandleMethod}
                            gameState={state.gameState}
                            action={this.gameCloseHandler}
                            isMobile={this.isMobile || this.isIphone}
                            game_PayoutInfo={this.game_PayoutInfo}
                            assestsLoaderFunction={this.assestsLoaderFunction}
                            holdem_chipValues={this.chipValues}
                            minBetAmt={this.minBet}
                            maxBetAmt={this.maxBet}
                            holdem_minbetLbl={this.minBetLbl}
                            holdem_maxBetLbl={this.maxBetLbl}
                            pg_innerLimits={this.innerLimits}
                            language={this.props.language}
                            infoText={state.infoText}
                          />
                        </Route>

                        <Route exact path="/truco">
                          <Truco
                            ref={this.handleGameRef}
                            assestsLoaderFunction={this.assestsLoaderFunction}
                            network={this.connectionTo}
                            setPopUp={(msg) => this.setState({ closePopup: true, popupMessage: msg, showOkButton: true, popupBtnName: "Refresh" })}
                          />
                        </Route>

                      </Switch>

                      <PayOutInfo childRef={(ref) => (this.PayOutInfo = ref)} ref={this.PayOutInfo} isMobile={this.isMobile} />
                      {state.forceLog && <ForceLogin forceData={{ log: state.alertText, forcedLoginCheck: this.forcedLoginCheck }} />}

                      {state.showTrucoGame && <Truco
                        ref={this.handleGameRef}
                        assestsLoaderFunction={this.assestsLoaderFunction}
                        network={this.connectionTo}
                        setPopUp={(msg) => this.setState({ closePopup: true, popupMessage: msg, showOkButton: true, popupBtnName: "Refresh" })}
                      />}
                    </ContextAPI.Provider>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Loading overlay (stars + progress bar) ── */}
          {showLoader && (
            <div className="MainLoadercontainer">
              <div className="Loadercontainer">
                <div className="stars" /><div className="stars2" /><div className="stars3" />
              </div>
              <div className="MainLogoBalla">
                <img draggable="false" src={SITE_LOADERS[this.siteId] ?? BallaGamesLogo} alt="Logo" className="ballaImg" />
                <div className="star-progress">
                  <div className="star-progress-track">
                    <div className="star-progress-fill" style={{ width: `${state.progressValue}%` }} />
                    <div className="progress-star" style={{ left: `calc(${state.progressValue}% - 14px)` }}>
                      <span className="star-core">★</span>
                      <span className="star-edge">★</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {this.state.showTrucoWelcome && <WelcomeTrucoDialog
            isOpen={this.state.showTrucoWelcome}
            onSubmit={this.handleNicknameSubmit}
            defaultValue={this.state.savedNickname}
          />}
          {/* ── Pre-entry error popup ── */}
          {this.showpopIniterror && (
            <div className="Init">
              <div className={`popupWinInit ${state.showOkButton ? "popUpRefreshInit" : ""} ${state.closePopup ? "block" : "none"}`}>
                <div className="session_closedInit">
                  <img draggable="false" src={errorAlert} alt="Error" />
                  <div className="fd_Init session_closed_text_containerInit">{state.popupMessage}</div>
                  {(!navigator.onLine || state.showOkButton) && (
                    <button className="btn_2init m_t_15" onClick={() => state.showOkButton ? document.location.reload() : this.closeGames()}>
                      {state.showOkButton ? state.popupBtnName : "OK"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── "Away too long" popup ── */}
          <div className="awayPopup" id="awayPopup">
            <div className="popupTooLong">
              <div className="fd m_b_5 bold clr_1">You are in a Timer game</div>
              You were away too long — please re-open the game.
              <div className="fd btn_iosPopup">
                <button onClick={this.refreshGame}>Reconnect</button>
                <button onClick={this.closeWin}>Close window</button>
              </div>
            </div>
          </div>

          {/* ── Technical error popup (set via DOM, legacy) ── */}
          <div className="errorPopup" id="errorPopup">
            <div className="popupTooLong" id="errorPopupIn" />
          </div>

          {/* Force-login alert text */}
          <div onClick={this.forcedLoginCheck}
            style={{ zIndex: 300, position: "absolute", top: "40%", right: "40%", color: "#fff", fontSize: 24 }}>
            {state.alertText}
          </div>

        </div>

        {/* ── Rotate-device overlay ── */}
        <div className="isMobileTrue" id="isMobileTrue">
          <div className="isMob">
            <img src={mobile_tilt} alt="rotate device" />
            Please rotate your screen to portrait mode or turn off auto screen rotate.
          </div>
        </div>

      </React.Fragment>
    );
  }
}

// ─── Redux wiring ─────────────────────────────────────────────────────────────
const mapStateToProps = (state) => ({
  language: state.languageObjs.languageObj,
  noLanguage: state.languageObjs.languageError,
});

const mapDispatch = (dispatch) => ({
  fetchEnglish: () => dispatch(fetchEnglish()),
  fetchTelugu: () => dispatch(fetchTelugu()),
  fetchBengali: () => dispatch(fetchBengali()),
  fetchMarati: () => dispatch(fetchMarati()),
  fetchGujarati: () => dispatch(fetchGujarati()),
  fetchKannada: () => dispatch(fetchKannada()),
  fetchTamil: () => dispatch(fetchTamil()),
  fetchMalayalam: () => dispatch(fetchMalayalam()),
  fetchPortuguese: () => dispatch(fetchPortuguese()),
});

export default withRouter(connect(mapStateToProps, mapDispatch)(App));